import React, { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CoinSeller from "../component/dialog/CoinSeller.js";
import { OPEN_COIN_SELLER_DIALOGUE } from "../store/seller/seller.type.js";
import { permissionError } from "../util/Alert.js";

const DashboardIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const HostsIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const AddHostIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const HistoryIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export default function AgencyLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const seller = useSelector((state) => state.admin?.seller);

  const navItems = [
    {
      id: "dashboard",
      label: t("dashboard"),
      path: "/agencypanel/homePage",
      icon: DashboardIcon,
      match: (p) => p === "/agencypanel/homePage",
    },
    {
      id: "hosts",
      label: t("hosts") || "Hosts",
      path: "/agencypanel/creators",
      icon: HostsIcon,
      match: (p) => p.startsWith("/agencypanel/creators"),
    },
    {
      id: "add_host",
      label: t("add_host"),
      icon: AddHostIcon,
      action: "add_host",
      accent: true,
    },
    {
      id: "history",
      label: t("history"),
      path: "/agencypanel/hosthistory",
      icon: HistoryIcon,
      match: (p) =>
        p.startsWith("/agencypanel/hosthistory") ||
        p.startsWith("/agencypanel/Income"),
    },
  ];

  useEffect(() => {
    const canGo = location.pathname !== "/agencypanel/homePage";
    window.__androidCanGoBack = canGo;
    try {
      window.Android?.setCanGoBack?.(canGo);
    } catch {
      /* ignore */
    }
  }, [location]);

  useEffect(() => {
    const onAndroidBack = () => {
      if (location.pathname !== "/agencypanel/homePage") {
        navigate(-1);
        try {
          window.Android?.backHandled?.();
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("android-back", onAndroidBack);
    return () => window.removeEventListener("android-back", onAndroidBack);
  }, [location, navigate]);

  const onNavTap = (nav) => {
    if (nav.action === "add_host") {
      if (seller?.isActive === false) {
        permissionError();
        return;
      }
      dispatch({ type: OPEN_COIN_SELLER_DIALOGUE });
      return;
    }
    if (nav.path) navigate(nav.path);
  };

  const renderItem = (nav, isSidebar) => {
    const isActive = nav.match ? nav.match(location.pathname) : false;
    const classes = [
      "agency-nav-item",
      isSidebar ? "agency-nav-item--side" : "agency-nav-item--bottom",
      isActive ? "is-active" : "",
      nav.accent ? "agency-nav-item--accent" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        key={nav.id}
        type="button"
        className={classes}
        onClick={() => onNavTap(nav)}
        aria-label={nav.label}
        aria-current={isActive ? "page" : undefined}
      >
        <span className="agency-nav-icon-wrap" aria-hidden="true">
          {nav.icon}
        </span>
        <span className="agency-nav-label">{nav.label}</span>
      </button>
    );
  };

  return (
    <div className="agency-shell">
      <aside className="sidebar-nav" aria-label="Agency sidebar">
        <h2 className="agency-side-title">Agency</h2>
        {navItems.map((nav) => renderItem(nav, true))}
      </aside>

      <main className="agency-main">
        <Outlet />
      </main>

      <nav className="bottom-nav" aria-label="Agency navigation">
        {navItems.map((nav) => renderItem(nav, false))}
      </nav>

      {/* Available from every tab so bottom Add Host always works */}
      <CoinSeller />
    </div>
  );
}
